import React, { useState, useEffect, useCallback } from "react";
import AdminService from "../services/AdminService";
import { toast } from "react-toastify";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import "../styles/AdminAds.css";
import {
    FiSearch, FiUser, FiMail, FiPhone, FiMapPin,
    FiShield, FiDollarSign, FiEye, FiMoreVertical,
    FiEdit2, FiCheckCircle, FiXCircle, FiLock,
    FiUnlock, FiClock, FiAlertCircle, FiChevronLeft, FiChevronRight, FiPlus,
    FiTrash2, FiInfo, FiExternalLink, FiPieChart, FiSettings
} from "react-icons/fi";

/**
 * Normalize a raw UserResponse from the backend to the shape UserManagement expects.
 */
const normalizeUser = (u) => ({
    ...u,
    phone: u.phone || "",
    coins: u.coinBalance ?? 0,
    status: u.accountBlocked ? "blocked" : "active",
    verified: u.emailVerified || false,
    joinedDate: u.joinedDate ? new Date(u.joinedDate).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
    }) : "N/A",
    roleDisplay: u.role?.replace('ROLE_', '') || "USER",
    statusRemark: u.statusRemark || "",
    planName: u.planName || "Basic" // Default to Basic if not available
});

const UserManagement = () => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [statsLoading, setStatsLoading] = useState(true);

    // Filters & Pagination
    const [searchTerm, setSearchTerm] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [selectedUser, setSelectedUser] = useState(null);
    const [userDetails, setUserDetails] = useState(null);
    const [loadingDetails, setLoadingDetails] = useState(false);
    const [plans, setPlans] = useState([]);
    const [planFilter, setPlanFilter] = useState("all");

    // Modals
    const [showUserModal, setShowUserModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showWalletModal, setShowWalletModal] = useState(false);
    const [showBlockModal, setShowBlockModal] = useState(false);
    const [blockAction, setBlockAction] = useState({ type: "block", remark: "" });
    const [walletAction, setWalletAction] = useState({ type: "credit", amount: "", reason: "" });
    const [editForm, setEditForm] = useState({ name: "", email: "", mobile: "", code: "" });

    // Pagination State
    const [currentPage, setCurrentPage] = useState(0);
    const [pageSize] = useState(15);
    const [pagination, setPagination] = useState({ totalPages: 1, totalItems: 0 });

    const [userStats, setUserStats] = useState({
        totalUsers: 0,
        activeUsers: 0,
        totalBuying: 0,
        totalSelling: 0
    });

    const loadStats = useCallback(async () => {
        setStatsLoading(true);
        try {
            const res = await AdminService.getUserStats();
            const statsData = res.data?.data || {};
            setUserStats({
                totalUsers: statsData.totalUsers || 0,
                activeUsers: statsData.activeUsers || 0,
                totalBuying: statsData.totalBuying || 0,
                totalSelling: statsData.totalSelling || 0
            });
        } catch (error) {
            console.error("Failed to load user management stats:", error);
        } finally {
            setStatsLoading(false);
        }
    }, []);

    useEffect(() => {
        const fetchPlans = async () => {
            try {
                const response = await AdminService.getPlans();
                setPlans(response.data?.data || []);
            } catch (error) {
                console.error("Failed to fetch plans:", error);
            }
        };
        fetchPlans();
    }, []);

    const loadUsers = useCallback(async (page = currentPage) => {
        setLoading(true);
        try {
            const response = await AdminService.getUsers({
                page,
                size: pageSize,
                search: searchTerm,
                status: statusFilter !== "all" ? statusFilter : undefined,
                planId: planFilter !== "all" ? planFilter : undefined
            });
            const apiRes = response.data;
            const data = apiRes.data || {};
            const rawUsers = data.content || [];

            setUsers(Array.isArray(rawUsers) ? rawUsers.map(normalizeUser) : []);
            setPagination({
                totalPages: data.totalPages ?? 1,
                totalItems: data.totalElements ?? (Array.isArray(rawUsers) ? rawUsers.length : 0),
            });
        } catch (error) {
            console.error("Failed to load users:", error);
            toast.error("Failed to load users");
        } finally {
            setLoading(false);
        }
    }, [currentPage, pageSize, searchTerm, statusFilter, planFilter]);

    useEffect(() => {
        loadStats();
    }, [loadStats]);

    useEffect(() => {
        const timer = setTimeout(() => {
            loadUsers(0);
        }, 500);
        return () => clearTimeout(timer);
    }, [searchTerm, statusFilter, loadUsers]);

    const handleViewUser = async (user) => {
        setSelectedUser(user);
        setShowUserModal(true);
        setLoadingDetails(true);
        try {
            const res = await AdminService.getUserById(user.id);
            setUserDetails(normalizeUser(res.data.data));
        } catch (error) {
            toast.error("Failed to load user details");
            setShowUserModal(false);
        } finally {
            setLoadingDetails(false);
        }
    };

    const handleEditUser = (user) => {
        setSelectedUser(user);
        setEditForm({
            id: user.id,
            name: user.name || "",
            email: user.email || "",
            mobile: user.mobile || "",
            code: user.code || ""
        });
        setShowEditModal(true);
    };

    const handleUpdateUser = async () => {
        try {
            await AdminService.updateUser(editForm);
            toast.success("User updated successfully");
            setShowEditModal(false);
            loadUsers();
        } catch (error) {
            toast.error(error.response?.data?.message || "Update failed");
        }
    };

    const handleDeleteUser = async (id) => {
        if (window.confirm("Are you sure you want to delete this user? This action cannot be undone.")) {
            try {
                await AdminService.deleteUser(id);
                toast.success("User deleted successfully");
                loadUsers();
            } catch (error) {
                toast.error("Delete failed");
            }
        }
    };

    const handleBlockToggle = (user) => {
        setSelectedUser(user);
        setBlockAction({
            type: user.status === "blocked" ? "unblock" : "block",
            remark: ""
        });
        setShowBlockModal(true);
    };

    const confirmBlockToggle = async () => {
        try {
            if (blockAction.type === "block") {
                await AdminService.blockUser(selectedUser.id, blockAction.remark);
                toast.success(`${selectedUser.name} has been blocked`);
            } else {
                await AdminService.unblockUser(selectedUser.id, blockAction.remark);
                toast.success(`${selectedUser.name} has been unblocked`);
            }
            setShowBlockModal(false);
            loadUsers();
        } catch (error) {
            toast.error(`Action failed: ${error.message}`);
        }
    };

    const handleWalletAction = async () => {
        if (!walletAction.amount || parseFloat(walletAction.amount) <= 0) {
            toast.error("Please enter a valid amount");
            return;
        }
        try {
            const data = {
                userId: selectedUser.id,
                amount: parseFloat(walletAction.amount),
                reason: walletAction.reason,
            };
            if (walletAction.type === "credit") {
                await AdminService.creditWallet(data);
            } else {
                // Using the new debit endpoint
                await AdminService.debitWallet(data);
            }
            toast.success(`Wallet ${walletAction.type}ed successfully`);
            setShowWalletModal(false);
            setWalletAction({ type: "credit", amount: "", reason: "" });
            loadUsers();
        } catch (error) {
            toast.error(error.response?.data?.message || `Wallet ${walletAction.type} failed`);
        }
    };

    return (
        <div className="admin-ads-container">
            {/* Header */}
            {/* <div className="admin-header">
                <div>
                    <h1 className="admin-title">User Management</h1>
                    <p className="admin-subtitle">Monitor and manage platform members</p>
                </div>
            </div> */}

            {/* KPI Stats Grid */}
            <div className="admin-stats-grid">
                {[
                    { label: "Total Users", value: userStats.totalUsers, icon: FiUser, color: "blue", sub: "Registered Accounts" },
                    { label: "Active Users", value: userStats.activeUsers, icon: FiCheckCircle, color: "green", sub: "Currently Unblocked" },
                    { label: "Total Spent", value: `$${userStats.totalBuying?.toLocaleString() || '0'}`, icon: FiDollarSign, color: "amber", sub: "Market Activity" },
                    { label: "Ads Posted", value: userStats.totalSelling, icon: FiPieChart, color: "rose", sub: "Selling Activity" }
                ].map((stat, i) => (
                    <div key={i} className="stats-card">
                        <div className={`stats-icon-box bg-${stat.color}`}>
                            {statsLoading ? <Skeleton circle width={24} height={24} /> : <stat.icon size={20} />}
                        </div>
                        <div className="stats-info">
                            <span className="stats-value">{statsLoading ? <Skeleton width={60} /> : stat.value}</span>
                            <span className="stats-label">{stat.label}</span>
                            <small className="admin-subtitle" style={{ fontSize: '10px' }}>{stat.sub}</small>
                        </div>
                    </div>
                ))}
            </div>

            {/* Filters */}
            <div className="premium-card" style={{ marginBottom: "20px" }}>
                <div className="filters-header">
                    <div className="search-field">
                        <FiSearch className="search-icon" />
                        <input
                            type="text"
                            placeholder="Search by name, email, or ID..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                    <select
                        className="pag-btn"
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                    >
                        <option value="all">All Status</option>
                        <option value="active">Active Only</option>
                        <option value="blocked">Blocked Only</option>
                    </select>
                    <select
                        className="pag-btn"
                        value={planFilter}
                        onChange={(e) => setPlanFilter(e.target.value)}
                    >
                        <option value="all">All Plans</option>
                        {plans.map(p => (
                            <option key={p.subscription.id} value={p.subscription.id}>{p.subscription.type}</option>
                        ))}
                    </select>
                    <button className="pag-btn" onClick={() => { setSearchTerm(""); setStatusFilter("all"); setPlanFilter("all"); }}>
                        Reset
                    </button>
                </div>
            </div>

            {/* Users Table */}
            <div className="premium-card overflow-hidden">
                <table className="premium-table">
                    <thead>
                        <tr>
                            <th>User Basics</th>
                            <th>Contact Details</th>
                            <th>Active Plan</th>
                            <th>Coin Balance</th>
                            <th>Growth & Role</th>
                            <th>Status</th>
                            <th style={{ textAlign: 'right' }}>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            [...Array(pageSize)].map((_, i) => (
                                <tr key={i}>
                                    <td><Skeleton count={2} width={150} /></td>
                                    <td><Skeleton count={2} width={120} /></td>
                                    <td><Skeleton width={80} /></td>
                                    <td><Skeleton width={100} /></td>
                                    <td><Skeleton width={70} /></td>
                                    <td><div className="action-btn-group"><Skeleton circle width={30} height={30} count={3} /></div></td>
                                </tr>
                            ))
                        ) : users.length > 0 ? (
                            users.map(user => (
                                <tr key={user.id}>
                                    <td>
                                        <div className="ad-text-info">
                                            <span className="ad-id">USR-{user.id}</span>
                                            <span className="ad-title-link" onClick={() => handleViewUser(user)} style={{ cursor: 'pointer' }}>
                                                {user.name}
                                            </span>
                                            <span className="ad-date" style={{ color: '#94a3b8', fontSize: '11px' }}>
                                                Joined {user.joinedDate}
                                            </span>
                                        </div>
                                    </td>
                                    <td>
                                        <div className="ad-text-info">
                                            <span className="user-subtext" style={{ fontSize: '13px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                <FiMail size={12} /> {user.email}
                                            </span>
                                            <span className="user-subtext" style={{ fontSize: '12px', display: 'flex', alignItems: 'center', gap: '4px', color: '#64748b' }}>
                                                <FiPhone size={12} /> {user.phone || "No Phone"}
                                            </span>
                                        </div>
                                    </td>
                                    <td>
                                        <div className="ad-text-info">
                                            <span style={{
                                                fontWeight: 600,
                                                color: user.planName === 'Premium' ? '#8b5cf6' : '#64748b',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '4px'
                                            }}>
                                                <FiShield size={14} /> {user.planName}
                                            </span>
                                        </div>
                                    </td>
                                    <td>
                                        <div className="ad-text-info">
                                            <span style={{ fontWeight: 700, color: 'var(--admin-primary)', fontSize: '15px' }}>
                                                {user.coins}
                                            </span>
                                            <span style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#94a3b8' }}>
                                                Credits
                                            </span>
                                        </div>
                                    </td>
                                    <td>
                                        <div className="ad-text-info">
                                            <span className="category-tag" style={{ alignSelf: 'flex-start' }}>{user.roleDisplay}</span>
                                            {user.verified && (
                                                <span style={{ fontSize: '10px', color: '#10b981', display: 'flex', alignItems: 'center', gap: '2px' }}>
                                                    <FiCheckCircle size={10} /> Verified
                                                </span>
                                            )}
                                        </div>
                                    </td>
                                    <td>
                                        <span className={`status-badge ${user.status === 'blocked' ? 'badge-rejected' : 'badge-accepted'}`}>
                                            {user.status === 'blocked' ? 'Blocked' : 'Active'}
                                        </span>
                                    </td>
                                    <td>
                                        <div className="action-btn-group">
                                            <button className="admin-btn-icon" title="View Details" onClick={() => handleViewUser(user)}>
                                                <FiEye />
                                            </button>
                                            <button className="admin-btn-icon" title="Edit User" onClick={() => handleEditUser(user)}>
                                                <FiEdit2 />
                                            </button>
                                            <button className="admin-btn-icon" title="Wallet" onClick={() => { setSelectedUser(user); setShowWalletModal(true); }}>
                                                <FiDollarSign />
                                            </button>
                                            <button
                                                className="admin-btn-icon"
                                                title={user.status === "blocked" ? "Unblock" : "Block"}
                                                onClick={() => handleBlockToggle(user)}
                                                style={{ color: user.status === "blocked" ? "#10b981" : "#ef4444" }}
                                            >
                                                {user.status === "blocked" ? <FiUnlock /> : <FiLock />}
                                            </button>
                                            <button className="admin-btn-icon" title="Delete User" onClick={() => handleDeleteUser(user.id)} style={{ color: "#ef4444" }}>
                                                <FiTrash2 />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="6">
                                    <div className="empty-state-container">
                                        <FiAlertCircle className="empty-state-icon" />
                                        <p>No users found matching your search</p>
                                    </div>
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>

                {/* Pagination */}
                {!loading && pagination.totalPages > 1 && (
                    <div className="pagination-container">
                        <div className="admin-subtitle">
                            Found {pagination.totalItems} users
                        </div>
                        <div className="pagination-numbers">
                            <button disabled={currentPage === 0} onClick={() => setCurrentPage(p => p - 1)} className="pag-btn">
                                <FiChevronLeft />
                            </button>
                            {[...Array(pagination.totalPages)].map((_, i) => {
                                // Simple sliding window for page numbers
                                if (i === 0 || i === pagination.totalPages - 1 || (i >= currentPage - 2 && i <= currentPage + 2)) {
                                    return (
                                        <button key={i} className={`pag-btn ${currentPage === i ? 'active' : ''}`} onClick={() => setCurrentPage(i)}>
                                            {i + 1}
                                        </button>
                                    );
                                } else if (i === currentPage - 3 || i === currentPage + 3) {
                                    return <span key={i} style={{ padding: '8px' }}>...</span>;
                                }
                                return null;
                            })}
                            <button disabled={currentPage === pagination.totalPages - 1} onClick={() => setCurrentPage(p => p + 1)} className="pag-btn">
                                <FiChevronRight />
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* View Modal */}
            {showUserModal && selectedUser && (
                <div className="admin-modal-overlay" onClick={() => setShowUserModal(false)}>
                    <div className="admin-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '600px' }}>
                        <div className="admin-modal-header">
                            <h3><FiInfo style={{ marginRight: '8px' }} /> User Profile</h3>
                            <button className="admin-modal-close" onClick={() => setShowUserModal(false)}>&times;</button>
                        </div>
                        <div className="admin-modal-body">
                            {loadingDetails ? (
                                <div style={{ padding: '20px' }}>
                                    <Skeleton count={5} height={30} style={{ marginBottom: '10px' }} />
                                </div>
                            ) : userDetails && (
                                <div className="user-details-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                                    <div className="detail-item">
                                        <label style={{ fontSize: '11px', color: '#94a3b8', textTransform: 'uppercase' }}>Full Name</label>
                                        <p style={{ margin: '4px 0', fontWeight: 600 }}>{userDetails.name}</p>
                                    </div>
                                    <div className="detail-item">
                                        <label style={{ fontSize: '11px', color: '#94a3b8', textTransform: 'uppercase' }}>User ID</label>
                                        <p style={{ margin: '4px 0' }}>USR-{userDetails.id}</p>
                                    </div>
                                    <div className="detail-item">
                                        <label style={{ fontSize: '11px', color: '#94a3b8', textTransform: 'uppercase' }}>Email Address</label>
                                        <p style={{ margin: '4px 0' }}>{userDetails.email}</p>
                                    </div>
                                    <div className="detail-item">
                                        <label style={{ fontSize: '11px', color: '#94a3b8', textTransform: 'uppercase' }}>Phone Number</label>
                                        <p style={{ margin: '4px 0' }}>{userDetails.phone || "Not Set"}</p>
                                    </div>
                                    <div className="detail-item">
                                        <label style={{ fontSize: '11px', color: '#94a3b8', textTransform: 'uppercase' }}>Wallet Balance</label>
                                        <p style={{ margin: '4px 0', color: 'var(--admin-primary)', fontWeight: 700 }}>{userDetails.coins} Credits</p>
                                    </div>
                                    <div className="detail-item">
                                        <label style={{ fontSize: '11px', color: '#94a3b8', textTransform: 'uppercase' }}>Account Status</label>
                                        <p style={{ margin: '4px 0' }}>
                                            <span className={`status-badge ${userDetails.status === 'blocked' ? 'badge-rejected' : 'badge-accepted'}`}>
                                                {userDetails.status}
                                            </span>
                                        </p>
                                    </div>
                                    {userDetails.status === 'blocked' && userDetails.statusRemark && (
                                        <div className="detail-item" style={{ gridColumn: 'span 2', background: '#fef2f2', padding: '10px', borderRadius: '8px', border: '1px solid #fee2e2' }}>
                                            <label style={{ fontSize: '11px', color: '#b91c1c', fontWeight: 700 }}>BLOCK REASON</label>
                                            <p style={{ margin: '4px 0', fontSize: '13px', color: '#991b1b' }}>{userDetails.statusRemark}</p>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                        <div className="admin-modal-footer">
                            <button className="btn-secondary" onClick={() => setShowUserModal(false)}>Close</button>
                            <button className="btn-primary" onClick={() => { setShowUserModal(false); handleEditUser(userDetails || selectedUser); }}>
                                Edit Profile
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Modal */}
            {showEditModal && selectedUser && (
                <div className="admin-modal-overlay" onClick={() => setShowEditModal(false)}>
                    <div className="admin-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '450px' }}>
                        <div className="admin-modal-header">
                            <h3><FiEdit2 style={{ marginRight: '8px' }} /> Update User</h3>
                            <button className="admin-modal-close" onClick={() => setShowEditModal(false)}>&times;</button>
                        </div>
                        <div className="admin-modal-body">
                            <div className="admin-form-group">
                                <label>Full Name</label>
                                <input
                                    type="text"
                                    className="admin-search-input"
                                    style={{ width: '100%' }}
                                    value={editForm.name}
                                    onChange={e => setEditForm({ ...editForm, name: e.target.value })}
                                />
                            </div>
                            <div className="admin-form-group">
                                <label>Email Address</label>
                                <input
                                    type="email"
                                    className="admin-search-input"
                                    style={{ width: '100%' }}
                                    value={editForm.email}
                                    onChange={e => setEditForm({ ...editForm, email: e.target.value })}
                                />
                            </div>
                            <div className="admin-form-group" style={{ display: 'grid', gridTemplateColumns: '80px 1fr', gap: '10px' }}>
                                <div>
                                    <label>Code</label>
                                    <input
                                        type="text"
                                        className="admin-search-input"
                                        style={{ width: '100%' }}
                                        value={editForm.code}
                                        onChange={e => setEditForm({ ...editForm, code: e.target.value })}
                                        placeholder="+1"
                                    />
                                </div>
                                <div>
                                    <label>Mobile Number</label>
                                    <input
                                        type="text"
                                        className="admin-search-input"
                                        style={{ width: '100%' }}
                                        value={editForm.mobile}
                                        onChange={e => setEditForm({ ...editForm, mobile: e.target.value })}
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="admin-modal-footer">
                            <button className="btn-secondary" onClick={() => setShowEditModal(false)}>Cancel</button>
                            <button className="btn-primary" onClick={handleUpdateUser}>Save Changes</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Block Modal */}
            {showBlockModal && selectedUser && (
                <div className="admin-modal-overlay" onClick={() => setShowBlockModal(false)}>
                    <div className="admin-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '450px' }}>
                        <div className="admin-modal-header">
                            <h3 style={{ color: blockAction.type === 'block' ? '#ef4444' : '#10b981' }}>
                                {blockAction.type === 'block' ? <FiLock /> : <FiUnlock />} {blockAction.type === 'block' ? 'Block User' : 'Unblock User'}
                            </h3>
                            <button className="admin-modal-close" onClick={() => setShowBlockModal(false)}>&times;</button>
                        </div>
                        <div className="admin-modal-body">
                            <p style={{ fontSize: '14px', marginBottom: '15px' }}>
                                {blockAction.type === 'block'
                                    ? `You are about to block ${selectedUser.name}. They will not be able to log in or post ads.`
                                    : `You are about to unblock ${selectedUser.name}. They will regain full access to their account.`}
                            </p>
                            <div className="admin-form-group">
                                <label>Reason / Internal Note</label>
                                <textarea
                                    className="admin-search-input"
                                    style={{ width: '100%', height: '80px', padding: '10px' }}
                                    placeholder="Enter reason for this action..."
                                    value={blockAction.remark}
                                    onChange={e => setBlockAction({ ...blockAction, remark: e.target.value })}
                                />
                            </div>
                        </div>
                        <div className="admin-modal-footer">
                            <button className="btn-secondary" onClick={() => setShowBlockModal(false)}>Cancel</button>
                            <button
                                className={`btn-primary ${blockAction.type === 'block' ? 'btn-danger' : 'btn-success'}`}
                                onClick={confirmBlockToggle}
                                style={{ backgroundColor: blockAction.type === 'block' ? '#ef4444' : '#10b981' }}
                            >
                                Confirm {blockAction.type === 'block' ? 'Block' : 'Unblock'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Wallet Modal */}
            {showWalletModal && selectedUser && (
                <div className="admin-modal-overlay" onClick={() => setShowWalletModal(false)}>
                    <div className="admin-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '450px' }}>
                        <div className="admin-modal-header">
                            <h3><FiDollarSign /> Wallet — {selectedUser.name}</h3>
                            <button className="admin-modal-close" onClick={() => setShowWalletModal(false)}>&times;</button>
                        </div>
                        <div className="admin-modal-body">
                            <div style={{ background: '#f8fafc', padding: '15px', borderRadius: '12px', marginBottom: '20px', textAlign: 'center' }}>
                                <small style={{ color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Current Credits</small>
                                <h2 style={{ color: 'var(--admin-primary)', margin: '5px 0' }}>{selectedUser.coins}</h2>
                            </div>

                            <div className="admin-form-group">
                                <label>Action Type</label>
                                <div style={{ display: 'flex', gap: '10px' }}>
                                    <button
                                        className={`pag-btn ${walletAction.type === 'credit' ? 'active' : ''}`}
                                        style={{ flex: 1 }}
                                        onClick={() => setWalletAction({ ...walletAction, type: 'credit' })}
                                    >
                                        Credit (+)
                                    </button>
                                    <button
                                        className={`pag-btn ${walletAction.type === 'debit' ? 'active' : ''}`}
                                        style={{ flex: 1 }}
                                        onClick={() => setWalletAction({ ...walletAction, type: 'debit' })}
                                    >
                                        Debit (-)
                                    </button>
                                </div>
                            </div>

                            <div className="admin-form-group">
                                <label>Amount</label>
                                <input
                                    type="number"
                                    className="admin-search-input"
                                    style={{ width: '100%' }}
                                    placeholder="Enter amount..."
                                    value={walletAction.amount}
                                    onChange={e => setWalletAction({ ...walletAction, amount: e.target.value })}
                                />
                            </div>

                            <div className="admin-form-group">
                                <label>Internal Note / Reason</label>
                                <input
                                    type="text"
                                    className="admin-search-input"
                                    style={{ width: '100%' }}
                                    placeholder="e.g. Compensation for issue #123"
                                    value={walletAction.reason}
                                    onChange={e => setWalletAction({ ...walletAction, reason: e.target.value })}
                                />
                            </div>
                        </div>
                        <div className="admin-modal-footer">
                            <button className="btn-secondary" onClick={() => setShowWalletModal(false)}>Cancel</button>
                            <button
                                className={`btn-primary ${walletAction.type === 'debit' ? 'btn-danger' : ''}`}
                                onClick={handleWalletAction}
                                style={{ backgroundColor: walletAction.type === 'debit' ? '#ef4444' : '' }}
                            >
                                Process Transaction
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <style>{`
                .btn-success {
                    background-color: #10b981;
                    color: white;
                    border: none;
                    border-radius: 8px;
                    padding: 8px 16px;
                    font-weight: 600;
                    cursor: pointer;
                }
                .btn-danger {
                    background-color: #ef4444;
                    color: white;
                    border: none;
                    border-radius: 8px;
                    padding: 8px 16px;
                    font-weight: 600;
                    cursor: pointer;
                }
                .user-subtext {
                    color: #64748b;
                }
                .admin-form-group {
                    margin-bottom: 15px;
                }
                .admin-form-group label {
                    display: block;
                    font-size: 12px;
                    font-weight: 600;
                    color: #475569;
                    margin-bottom: 6px;
                }
                .overflow-hidden {
                    overflow: hidden;
                }
            `}</style>
        </div>
    );
};

export default UserManagement;
